import Page from "../../../api/Page";
import Kill from "../../../api/kill/Kill";
import {DataTable, DataTableColumn, DataTableSortStatus} from "mantine-datatable";
import {Button, Group, NumberInput, Pagination, Stack, TextInput} from "@mantine/core";
import ListQuery from "../../../api/kill/ListQuery";
import DateTime from "../../components/_utils/date-time/DateTime";
import {useEffect, useState} from "react";

type KillViewProps = {
    page: Page<Kill> | undefined;

    /**
     * KillApi [ListQuery].
     * To filter.
     * @author Kacper Faber
     */
    query: ListQuery;

    /**
     * Update search query.
     * @author Kacper Faber
     * @param query
     */
    setQuery: (query: ListQuery) => void;

    loading: boolean;
}

/**
 * initial sorting status.
 * @author Kacper Faber
 */
const initSort: DataTableSortStatus = {
    columnAccessor: 'dateTime',
    direction: 'desc'
};

const isFilterActive = (value: string | number | undefined | null) =>
    value !== undefined && value !== null && value !== '';

/**
 * List of paginated kills.
 * @param props
 * @constructor
 */
function KillView(props: KillViewProps) {
    const [sortStatus, setSortStatus] = useState<DataTableSortStatus>(initSort);

    const updateQuery = (patch: Partial<ListQuery>) => {
        props.setQuery({...props.query, ...patch});
    }

    const columns: DataTableColumn[] = [
        {accessor: 'id', title: '#', sortable: true},
        {
            accessor: 'computer.title',
            title: 'Nazwa komputera',
            sortable: true,
            sortKey: 'computer__title',
            filter: _ => (
                <Stack>
                    <TextInput
                        placeholder="Nazwa komputera"
                        value={props.query.q ?? ''}
                        onChange={(e) => updateQuery({q: e.target.value || undefined})}
                    />

                    <Button
                        color="red"
                        size="xs"
                        onClick={() => updateQuery({q: undefined})}
                        children="Wyczyść"
                    />
                </Stack>
            ),
            filtering: isFilterActive(props.query.q),
        },
        {
            accessor: 'computer.userName',
            title: 'Nazwa użytkownika',
            sortable: true,
            filter: _ => (
                <Stack>
                    <TextInput
                        placeholder="Nazwa użytkownika"
                        value={props.query.computerUserName ?? ''}
                        onChange={(e) => updateQuery({computerUserName: e.target.value || undefined})}
                    />

                    <Button
                        color="red"
                        size="xs"
                        onClick={() => updateQuery({computerUserName: undefined})}
                        children="Wyczyść"
                    />
                </Stack>
            ),
            filtering: isFilterActive(props.query.computerUserName),
        },
        {
            accessor: 'computer.domainName',
            title: 'Domena',
            sortable: true,
            sortKey: 'computer__domain_name',
            filter: _ => (
                <Stack>
                    <TextInput
                        placeholder="Domena"
                        value={props.query.computerDomainName ?? ''}
                        onChange={(e) => updateQuery({computerDomainName: e.target.value || undefined})}
                    />

                    <Button
                        color="red"
                        size="xs"
                        onClick={() => updateQuery({computerDomainName: undefined})}
                        children="Wyczyść"
                    />
                </Stack>
            ),
            filtering: isFilterActive(props.query.computerDomainName),
        },
        {
            accessor: 'title',
            title: 'Program',
            sortable: true,
            sortKey: 'title',
            filter: _ => (
                <Stack>
                    <TextInput
                        placeholder="Program"
                        value={props.query.title ?? ''}
                        onChange={(e) => updateQuery({title: e.target.value || undefined})}
                    />

                    <Button
                        color="red"
                        size="xs"
                        onClick={() => updateQuery({title: undefined})}
                        children="Wyczyść"
                    />
                </Stack>
            ),
            filtering: isFilterActive(props.query.title),
        },
        {
            accessor: 'dateTime',
            title: 'Czas zakończenia',
            sortable: true,
            render: r => <DateTime dateTime={(r as any).dateTime} />,
            sortKey: 'date_time'
        },
        {
            accessor: 'processId',
            title: 'ID Procesu (PID)',
            filter: _ => (
                <Stack>
                    <NumberInput
                        placeholder="PID"
                        value={props.query.processId}
                        onChange={(value) => updateQuery({
                            processId: typeof value === 'number' ? value : undefined
                        })}
                        allowDecimal={false}
                        allowNegative={false}
                    />

                    <Button
                        color="red"
                        size="xs"
                        onClick={() => updateQuery({processId: undefined})}
                        children="Wyczyść"
                    />
                </Stack>
            ),
            filtering: isFilterActive(props.query.processId),
        },
        {
            accessor: 'processName',
            title: 'Nazwa procesu',
            filter: _ => (
                <Stack>
                    <TextInput
                        placeholder="Nazwa procesu"
                        value={props.query.processName ?? ''}
                        onChange={(e) => updateQuery({processName: e.target.value || undefined})}
                    />

                    <Button
                        color="red"
                        size="xs"
                        onClick={() => updateQuery({processName: undefined})}
                        children="Wyczyść"
                    />
                </Stack>
            ),
            filtering: isFilterActive(props.query.processName),
            sortable: true,
            sortKey: 'process_name'
        },
    ];

    const reloadSort = () => {
        props.setQuery({...props.query, sortBy: sortStatus.sortKey, sortDir: sortStatus.direction});
    }

    useEffect(reloadSort, [sortStatus]);

    return (
        <>
            <DataTable
                backgroundColor="transparent"
                styles={{header: {background: 'var(--mantine-color-dark-7)'}}}
                withColumnBorders
                highlightOnHover
                columns={columns}
                records={props.page?.results}
                fetching={props.loading}
                sortStatus={sortStatus}
                onSortStatusChange={setSortStatus}
                height="50vh"
                noRecordsText="Ale pusto..."
                mb="md"
            />

            <Group justify="center">
                <Pagination
                    total={props.page?.totalPages ?? 1}
                    onChange={(e) => props.setQuery({...props.query, page: e})}
                />
            </Group>
        </>
    );
}

export default KillView;

// TODO: Implement KillView.tsx
